// 海岸線からの距離で機体が「陸/領海/公海」のどれにいるかを判定し、公海(高速)か
// 否かの速度ポリシーまで own するローカル分類器。
//
// 機体の lat/lng（game.js が worldToLatLng で計算済み）を、同梱した陸地ポリゴン
// (public/data/land.geojson, tools/build-land で生成) と照合する。呼び出し側は
// 毎フレーム update() を呼び、上限速度は isHighSeas ゲッターを見るだけでよい：
//   update(lat,lng,x,z) … 移動 or 一定間隔ごとに再判定し、ヒステリシス付きで
//                          isHighSeas を更新（陸・川・湖は常に領海=false）。
//   isHighSeas          … 公海なら true。既定は false（＝領海 900km/h）。
//   classify(lat,lng)   … 素の幾何判定 {overLand, distM}（デバッグ/テスト用）。
//
// データは「フラットリング」形式 { rings: [[lng,lat,lng,lat,...], ...] }。全リング
// （大陸・島・穴）を1つの集合として even-odd 判定＝互いに素な陸塊も穴も正しく処理。
// load() で緯度1°帯(row)・緯度経度1°格子(cell)の2索引を一度だけ構築し、判定は
// 同期・軽量（1帯＋近傍セルだけ走査）。
//
// 既知の制約：東向きレイ／セル近傍探索は対蹠経線(±180°)をまたがない。日本
// (122〜154°E) には無関係。太平洋横断を厳密化する場合は経度正規化が必要。

// --- 速度ポリシー（12海里=22.224km の領海境界を挟むヒステリシスで境界のちらつきを防ぐ） ---
const HIGH_SEAS_ENTER_M = 24000; // 海岸から 24km 超で公海(高速)を解禁
const HIGH_SEAS_EXIT_M = 20000;  // 20km 未満で領海(900) へ戻す（20 < 22.224 < 24）
const SEARCH_M = 30000;          // 最寄り海岸探索の窓（enter 24km に余裕）
const MIN_MOVE_M = 500;          // 再判定スロットル: これだけ動いた or
const MIN_INTERVAL_MS = 250;     //                  この間隔が経過したら再判定（数Hz）

// 数値セルキー: latCell∈[-90,89], lngCell∈[-180,179] を一意な整数に。
const cellKey = (cy, cx) => (cy + 90) * 400 + (cx + 180);

export function createCoastlineClassifier() {
  let ready = false;
  let rowIndex = null;   // Map<latCell, Float32Array [x1,y1,x2,y2,...]>  (even-odd 用)
  let cellIndex = null;  // Map<cellKey, Float32Array [x1,y1,x2,y2,...]>  (最短距離用)
  // 速度ポリシーの状態（この分類器が own する）。lastX/Z=Infinity で初回は必ず判定。
  let isHighSeas = false;
  let lastX = Infinity, lastZ = Infinity, lastAt = 0;

  function build(rings) {
    const rowTmp = new Map();
    const cellTmp = new Map();
    const push = (map, key, x1, y1, x2, y2) => {
      let a = map.get(key);
      if (!a) map.set(key, (a = []));
      a.push(x1, y1, x2, y2);
    };
    for (const r of rings) {
      for (let i = 0; i + 3 < r.length; i += 2) {
        const x1 = r[i], y1 = r[i + 1], x2 = r[i + 2], y2 = r[i + 3];
        const latLo = Math.floor(Math.min(y1, y2));
        const latHi = Math.floor(Math.max(y1, y2));
        const lngLo = Math.floor(Math.min(x1, x2));
        const lngHi = Math.floor(Math.max(x1, x2));
        for (let b = latLo; b <= latHi; b++) push(rowTmp, b, x1, y1, x2, y2);
        for (let cy = latLo; cy <= latHi; cy++)
          for (let cx = lngLo; cx <= lngHi; cx++)
            push(cellTmp, cellKey(cy, cx), x1, y1, x2, y2);
      }
    }
    // Float32 で保持（座標は |180| 以下・元データ ~11m 丸めなので精度十分、メモリ半減）。
    rowIndex = new Map();
    for (const [k, a] of rowTmp) rowIndex.set(k, Float32Array.from(a));
    cellIndex = new Map();
    for (const [k, a] of cellTmp) cellIndex.set(k, Float32Array.from(a));
  }

  // even-odd 点内包（東向き水平レイ）。緯度帯の線分だけ走査。
  function overLand(lat, lng) {
    const seg = rowIndex.get(Math.floor(lat));
    if (!seg) return false;
    let inside = false;
    for (let i = 0; i < seg.length; i += 4) {
      const y1 = seg[i + 1], y2 = seg[i + 3];
      if ((y1 > lat) !== (y2 > lat)) {
        const x1 = seg[i], x2 = seg[i + 2];
        const xc = x1 + ((lat - y1) / (y2 - y1)) * (x2 - x1);
        if (xc > lng) inside = !inside;
      }
    }
    return inside;
  }

  // 最寄り海岸距離(m)。近傍セルの線分を局所等距円筒(m)に変換し点-線分距離の最小値。
  // 窓内に海岸が無ければ Infinity（下流の enter 判定で公海扱い）。
  function distMeters(lat, lng) {
    const cos = Math.cos((lat * Math.PI) / 180);
    const mLat = 110540, mLng = 111320 * cos;
    const latCell = Math.floor(lat), lngCell = Math.floor(lng);
    // 経度セル幅は cos(lat) で縮むので高緯度ほど窓を広げる。
    const lngCells = Math.max(1, Math.ceil(SEARCH_M / (111320 * Math.max(cos, 0.05))));
    let best = Infinity;
    for (let cy = latCell - 1; cy <= latCell + 1; cy++) {
      for (let dx = -lngCells; dx <= lngCells; dx++) {
        const seg = cellIndex.get(cellKey(cy, lngCell + dx));
        if (!seg) continue;
        for (let i = 0; i < seg.length; i += 4) {
          const ax = (seg[i] - lng) * mLng, ay = (seg[i + 1] - lat) * mLat;
          const bx = (seg[i + 2] - lng) * mLng, by = (seg[i + 3] - lat) * mLat;
          const ex = bx - ax, ey = by - ay;
          const len2 = ex * ex + ey * ey;
          let t = len2 > 0 ? -(ax * ex + ay * ey) / len2 : 0;
          t = t < 0 ? 0 : t > 1 ? 1 : t;
          const cx2 = ax + t * ex, cy2 = ay + t * ey;
          const d2 = cx2 * cx2 + cy2 * cy2;
          if (d2 < best) best = d2;
        }
      }
    }
    return Math.sqrt(best);
  }

  return {
    get ready() {
      return ready;
    },
    get isHighSeas() {
      return isHighSeas;
    },
    // fetch＋索引構築。失敗しても throw せず false（呼び出し側のフレームループを守る）。
    async load(url) {
      try {
        const res = await fetch(url);
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        if (!data?.rings?.length) throw new Error('empty rings');
        build(data.rings);
        ready = true;
        return true;
      } catch (e) {
        console.warn('[coastline] load failed:', e);
        return false;
      }
    },
    // 素の幾何判定（ポリシー非適用）。デバッグ/テスト用。ready 前は null。
    classify(lat, lng) {
      if (!ready) return null;
      if (overLand(lat, lng)) return { overLand: true, distM: 0 };
      return { overLand: false, distM: distMeters(lat, lng) };
    },
    // 毎フレーム呼ぶ。移動 or 間隔でスロットルし、ヒステリシス付きで isHighSeas を更新。
    update(lat, lng, x, z) {
      if (!ready) return;
      const moved = (x - lastX) ** 2 + (z - lastZ) ** 2 >= MIN_MOVE_M * MIN_MOVE_M;
      const now = performance.now();
      if (!moved && now - lastAt < MIN_INTERVAL_MS) return;
      lastX = x; lastZ = z; lastAt = now;
      if (overLand(lat, lng)) isHighSeas = false;              // 陸・川・湖 → 領海
      else {
        const d = distMeters(lat, lng);
        if (d < HIGH_SEAS_EXIT_M) isHighSeas = false;          // 20km 未満 → 領海
        else if (d > HIGH_SEAS_ENTER_M) isHighSeas = true;     // 24km 超 → 公海
        // 20〜24km は現状維持（ちらつき防止）。
      }
    },
  };
}
